import { getScheduledPaymentGroups } from '@/services/batchPayment';
import { getScheduledPayments } from '@/services/scheduledPayment';
import { getPendingScheduledPaymentActions } from '@/services/multiSign';
import { getActiveScheduledWalletTransfers } from '@/services/scheduledWalletTransfer';
import { ScheduledBankPaymentGroupTypeEnum } from '@/features/payments/types/BatchPayment';
import { isFeatureEnabled } from '@/config/featureFlags.service';
import { Feature } from '@/config/featureFlags.definition';
import { TPaymentDataSource } from '../types/PaymentKindStrategy';

/**
 * The actual per-source `TPaymentDataSource` implementations - deliberately kept in their own
 * file, separate from data/paymentDataSources.ts's ordered list ("process"), so adding a new
 * source never means editing that process file's own logic, only adding a new export here and
 * one entry to its list.
 */

const TYPES = [ScheduledBankPaymentGroupTypeEnum.XERO, ScheduledBankPaymentGroupTypeEnum.EMERGE];

export const batchGroupsSource: TPaymentDataSource = {
  name: 'batchGroups',
  // Owner/Admin-only capability (see canAccessBatchPayments) - Personal profiles and business
  // Team Members never fetch batch groups at all, not just have them hidden client-side. This is
  // the same permission check that hides the "Batch Payment" quick action in MoveMoneyMenu.
  isEnabled: (ctx) => isFeatureEnabled(Feature.BATCH_PAYMENT) && ctx.hasBatchPaymentAccess,
  // states: [] omits the states param entirely (backend treats an empty/missing set as "no
  // filter"), so this single call returns the same union 3 filtered calls would.
  fetch: (ctx) => getScheduledPaymentGroups({ accountId: ctx.accountId, types: TYPES, states: [] })
};

// Individual payments that never belonged to a batch group. Passes both ids straight through -
// each service function itself prioritises businessId over accountId when both are present (see
// getScheduledPayments), since accountId only applies to a personal account.
export const scheduledPaymentsSource: TPaymentDataSource = {
  name: 'scheduledPayments',
  fetch: (ctx) => getScheduledPayments(ctx.accountId, ctx.businessId)
};

// Individual payments still awaiting multi-sign approval, sourced from the multi-sign action
// itself (no EScheduledBankPayment row exists yet at that point) - see
// PaymentRowKindEnum.PENDING_SCHEDULED_PAYMENT.
export const pendingScheduledPaymentActionsSource: TPaymentDataSource = {
  name: 'pendingScheduledPaymentActions',
  fetch: (ctx) => getPendingScheduledPaymentActions(ctx.accountId, ctx.businessId)
};

// Scheduled wallet-to-wallet transfers - unlike batch groups, never gated behind
// Feature.BATCH_PAYMENT/hasBatchPaymentAccess, same as the Transfer quick action in
// MoveMoneyMenu (requiresBusinessOwnerOrAdmin: false). Same accountId/businessId split as
// scheduledPaymentsSource above.
export const scheduledWalletTransfersSource: TPaymentDataSource = {
  name: 'scheduledWalletTransfers',
  fetch: (ctx) => getActiveScheduledWalletTransfers(ctx.accountId, ctx.businessId)
};
